/**
 * Palantir PMS - Software para la gestion de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creacion / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso esta restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import {
  FiAlertCircle,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiInfo,
  FiMinus,
  FiPlus,
} from "react-icons/fi";
import "../AvailabilitySummary.css";

const IMPACT_RANK = {
  low: 1,
  medium: 2,
  high: 3,
  very_high: 4,
};
// Experimental event bands above the table; set to false for fast rollback.
const EXPERIMENTAL_HEADER_EVENT_BANDS = true;
const MAX_SPECIAL_STRIP_LANES = 2;
const EVENT_LABEL_MAX_CHARS = 34;
const AVAILABILITY_HORIZON_DAYS = 365;

const AVAILABILITY_SUMMARY_MESSAGES = {
  en: {
    title: "Availability Summary",
    subtitleLoading: "Loading availability...",
    subtitleLoaded: (count, range) => `${count} categories | loaded range: ${range}`,
    noDates: "No dates",
    noDatesLoaded: "No dates loaded",
    specialDates: "Special dates",
    specialDate: "Special date",
    navigation: {
      backDays: (days) => `Back ${days} days`,
      backOneDay: "Back 1 day",
      forwardOneDay: "Forward 1 day",
      forwardDays: (days) => `Forward ${days} days`,
      resetToday: "Reset horizon to today",
      loadPrevious: (days) => `Load previous ${days} days horizon`,
      loadNext: (days) => `Load next ${days} days horizon`,
      today: "Today",
      shiftMinus: "Shift -",
      shiftPlus: "Shift +",
    },
    legend: {
      sold: "Sold",
      blocked: "Blocked",
      available: "Available",
    },
    readingHint: "The main number is rooms available. The bar compares available, sold and blocked inventory.",
    headers: {
      roomCategory: "Room Category",
    },
    hotel: {
      occupancyTitle: (pct, sold, total) => `Hotel occupancy ${pct}% (${sold} sold / ${total} total)`,
      occupancyShort: (pct) => `${pct}% occ`,
      availabilityTitle: (available, total, blocked) =>
        `Available ${available} / ${total} total | Blocked ${blocked}`,
      availabilityShort: (available, total) => `${available}/${total} free`,
    },
    category: {
      fallback: (id) => `Category ${id}`,
      inventory: (count) => `Inventory: ${count} rooms`,
    },
    day: {
      caption: (sold, blocked) => `S ${sold} | B ${blocked}`,
    },
    states: {
      loading: "Loading availability...",
      loadErrorTitle: "Could not load data.",
      noDataTitle: "No availability data.",
      noDataSubtitle: "There are no categories to display for this horizon.",
    },
    messages: {
      loadError: "Could not load availability summary.",
    },
    specialTooltip: ({ leadName, extras, names, start, end, impact }) => {
      const lines = [
        extras > 0 ? `${leadName} (+${extras})` : leadName,
        `${start} -> ${end}`,
        `Impact: ${impact}`,
      ];

      if (extras > 0 && names.length > 1) {
        lines.push(`Also: ${names.slice(1).join(", ")}`);
      }

      return lines.join("\n");
    },
    cellTooltip: ({ categoryName, date, total, sellable, available, sold, blocked }) =>
      [
        `${categoryName} - ${date}`,
        `Total: ${total}`,
        `Sellable: ${sellable}`,
        `Available: ${available}`,
        `Sold: ${sold}`,
        `Blocked: ${blocked}`,
      ].join("\n"),
  },
  es: {
    title: "Resumen de disponibilidad",
    subtitleLoading: "Cargando disponibilidad...",
    subtitleLoaded: (count, range) => `${count} categorías · rango cargado: ${range}`,
    noDates: "Sin fechas",
    noDatesLoaded: "Sin fechas cargadas",
    specialDates: "Fechas especiales",
    specialDate: "Fecha especial",
    navigation: {
      backDays: (days) => `Retroceder ${days} días`,
      backOneDay: "Retroceder 1 día",
      forwardOneDay: "Avanzar 1 día",
      forwardDays: (days) => `Avanzar ${days} días`,
      resetToday: "Restablecer el horizonte a hoy",
      loadPrevious: (days) => `Cargar el horizonte anterior de ${days} días`,
      loadNext: (days) => `Cargar el siguiente horizonte de ${days} días`,
      today: "Hoy",
      shiftMinus: "Mover -",
      shiftPlus: "Mover +",
    },
    legend: {
      sold: "Vendidas",
      blocked: "Bloqueadas",
      available: "Disponibles",
    },
    readingHint:
      "El número principal son las habitaciones disponibles. La barra compara inventario disponible, vendido y bloqueado.",
    headers: {
      roomCategory: "Categoría de habitación",
    },
    hotel: {
      occupancyTitle: (pct, sold, total) =>
        `Ocupación del hotel ${pct}% (${sold} vendidas / ${total} total)`,
      occupancyShort: (pct) => `${pct}% ocup.`,
      availabilityTitle: (available, total, blocked) =>
        `Disponibles ${available} / ${total} total | Bloqueadas ${blocked}`,
      availabilityShort: (available, total) => `${available}/${total} libres`,
    },
    category: {
      fallback: (id) => `Categoría ${id}`,
      inventory: (count) => `Inventario: ${count} habitaciones`,
    },
    day: {
      caption: (sold, blocked) => `V ${sold} | B ${blocked}`,
    },
    states: {
      loading: "Cargando disponibilidad...",
      loadErrorTitle: "No se han podido cargar los datos.",
      noDataTitle: "No hay datos de disponibilidad.",
      noDataSubtitle: "No hay categorías para mostrar en este horizonte.",
    },
    messages: {
      loadError: "No se ha podido cargar el resumen de disponibilidad.",
    },
    impactLabels: {
      low: "bajo",
      medium: "medio",
      high: "alto",
      very_high: "muy alto",
    },
    specialTooltip: ({ leadName, extras, names, start, end, impact, impactLabels }) => {
      const lines = [
        extras > 0 ? `${leadName} (+${extras})` : leadName,
        `${start} -> ${end}`,
        `Impacto: ${impactLabels?.[impact] || impact}`,
      ];

      if (extras > 0 && names.length > 1) {
        lines.push(`También: ${names.slice(1).join(", ")}`);
      }

      return lines.join("\n");
    },
    cellTooltip: ({ categoryName, date, total, sellable, available, sold, blocked }) =>
      [
        `${categoryName} - ${date}`,
        `Total: ${total}`,
        `Vendibles: ${sellable}`,
        `Disponibles: ${available}`,
        `Vendidas: ${sold}`,
        `Bloqueadas: ${blocked}`,
      ].join("\n"),
  },
  ca: {
    title: "Resum de disponibilitat",
    subtitleLoading: "Carregant disponibilitat...",
    subtitleLoaded: (count, range) => `${count} categories · rang carregat: ${range}`,
    noDates: "Sense dates",
    noDatesLoaded: "Sense dates carregades",
    specialDates: "Dates especials",
    specialDate: "Data especial",
    navigation: {
      backDays: (days) => `Retrocedir ${days} dies`,
      backOneDay: "Retrocedir 1 dia",
      forwardOneDay: "Avançar 1 dia",
      forwardDays: (days) => `Avançar ${days} dies`,
      resetToday: "Restablir l'horitzó a avui",
      loadPrevious: (days) => `Carregar l'horitzó anterior de ${days} dies`,
      loadNext: (days) => `Carregar el següent horitzó de ${days} dies`,
      today: "Avui",
      shiftMinus: "Moure -",
      shiftPlus: "Moure +",
    },
    legend: {
      sold: "Venudes",
      blocked: "Bloquejades",
      available: "Disponibles",
    },
    readingHint:
      "El número principal són les habitacions disponibles. La barra compara inventari disponible, venut i bloquejat.",
    headers: {
      roomCategory: "Categoria d'habitació",
    },
    hotel: {
      occupancyTitle: (pct, sold, total) =>
        `Ocupació de l'hotel ${pct}% (${sold} venudes / ${total} total)`,
      occupancyShort: (pct) => `${pct}% ocup.`,
      availabilityTitle: (available, total, blocked) =>
        `Disponibles ${available} / ${total} total | Bloquejades ${blocked}`,
      availabilityShort: (available, total) => `${available}/${total} lliures`,
    },
    category: {
      fallback: (id) => `Categoria ${id}`,
      inventory: (count) => `Inventari: ${count} habitacions`,
    },
    day: {
      caption: (sold, blocked) => `V ${sold} | B ${blocked}`,
    },
    states: {
      loading: "Carregant disponibilitat...",
      loadErrorTitle: "No s'han pogut carregar les dades.",
      noDataTitle: "No hi ha dades de disponibilitat.",
      noDataSubtitle: "No hi ha categories per mostrar en aquest horitzó.",
    },
    messages: {
      loadError: "No s'ha pogut carregar el resum de disponibilitat.",
    },
    impactLabels: {
      low: "baix",
      medium: "mitjà",
      high: "alt",
      very_high: "molt alt",
    },
    specialTooltip: ({ leadName, extras, names, start, end, impact, impactLabels }) => {
      const lines = [
        extras > 0 ? `${leadName} (+${extras})` : leadName,
        `${start} -> ${end}`,
        `Impacte: ${impactLabels?.[impact] || impact}`,
      ];

      if (extras > 0 && names.length > 1) {
        lines.push(`També: ${names.slice(1).join(", ")}`);
      }

      return lines.join("\n");
    },
    cellTooltip: ({ categoryName, date, total, sellable, available, sold, blocked }) =>
      [
        `${categoryName} - ${date}`,
        `Total: ${total}`,
        `Vendibles: ${sellable}`,
        `Disponibles: ${available}`,
        `Venudes: ${sold}`,
        `Bloquejades: ${blocked}`,
      ].join("\n"),
  },
};

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

export function addDaysIso(isoDate, days) {
  // Hotel calendar dates must not shift with a visitor's timezone or daylight saving.
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split("T")[0];
}

function buildDateRange(startIso, days) {
  const out = [];
  for (let i = 0; i < days; i += 1) {
    out.push(addDaysIso(startIso, i));
  }
  return out;
}

function detectDaysPerPage(width) {
  if (width < 576) return 3;
  if (width < 768) return 4;
  if (width < 992) return 5;
  if (width < 1120) return 6;
  return 7;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeDate(dateValue) {
  if (!dateValue) return "";
  return String(dateValue).slice(0, 10);
}

function toDayLabel(isoDate, locale) {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function isWeekendIso(isoDate) {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const day = date.getDay();
  return day === 0 || day === 6;
}

function occupancyToneClass(occupancyPct) {
  if (occupancyPct >= 90) return "high";
  if (occupancyPct >= 75) return "medium";
  return "normal";
}

function impactClass(level) {
  if (level === "very_high") return "veryHigh";
  if (level === "high") return "high";
  if (level === "medium") return "medium";
  return "low";
}

function clipEventLabel(label, maxChars = EVENT_LABEL_MAX_CHARS) {
  const text = String(label || "").trim();
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1)}…`;
}

function buildSpecialSegmentTooltip(segment, copy = AVAILABILITY_SUMMARY_MESSAGES.en) {
  const extras = Number(segment?.hiddenCount || 0);
  const names = Array.isArray(segment?.names) ? segment.names : [];
  const leadName = String(segment?.name || copy.specialDate || "Special date");
  const impact = String(segment?.impact || "medium");
  const start = String(segment?.clippedStart || "");
  const end = String(segment?.clippedEnd || "");

  return (copy.specialTooltip || AVAILABILITY_SUMMARY_MESSAGES.en.specialTooltip)({
    leadName,
    extras,
    names,
    start,
    end,
    impact,
    impactLabels: copy.impactLabels,
  });
}

function getDailyStats(row, index) {
  const total = Math.max(0, toNumber(row.inventorySeries[index], row.fixedInventory));
  const sellable = clamp(toNumber(row.activeSeries[index], total), 0, total);
  const available = clamp(toNumber(row.availableSeries[index], 0), 0, sellable);
  const blocked = clamp(total - sellable, 0, total);
  const sold = clamp(sellable - available, 0, total);

  return {
    total,
    sellable,
    available,
    blocked,
    sold,
  };
}


const AvailabilitySummary = () => {
  const { themeId, languageId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const availabilityCopy =
    AVAILABILITY_SUMMARY_MESSAGES[languageId] || AVAILABILITY_SUMMARY_MESSAGES.en;
  const calendarLocale = languageId === "en" ? undefined : language?.locale;
  const loadErrorMessageRef = useRef(availabilityCopy.messages.loadError);
  const pageRef = useRef(null);
  const [searchParams] = useSearchParams();
  const demoModeActive = isDemoMode();
  const businessUnitId = demoModeActive
    ? DEMO_BUSINESS_UNIT_ID
    : searchParams.get("business_unit_id") || "1";
  const referenceDate = (demoModeActive && demoReferenceDate()) || todayIso();

  const [startDate, setStartDate] = useState(() => referenceDate);
  const [daysPerPage, setDaysPerPage] = useState(7);
  const [windowStartIndex, setWindowStartIndex] = useState(0);

  const [roomCategoryMap, setRoomCategoryMap] = useState({});
  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [dates, setDates] = useState([]);
  const [cityEvents, setCityEvents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  useEffect(() => {
    loadErrorMessageRef.current = availabilityCopy.messages.loadError;
  }, [availabilityCopy.messages.loadError]);

  useEffect(() => {
    const updateDaysPerPage = (width) => {
      setDaysPerPage((prev) => {
        const next = detectDaysPerPage(width);
        return prev === next ? prev : next;
      });
    };

    const pageNode = pageRef.current;
    if (pageNode && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width || window.innerWidth || 1200;
        updateDaysPerPage(width);
      });
      observer.observe(pageNode);
      return () => observer.disconnect();
    }

    const onResize = () => updateDaysPerPage(window.innerWidth || 1200);
    onResize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function fetchCategories() {
      try {
        const res = await axios.get(`/api/room_categories?business_unit_id=${businessUnitId}`);
        if (!active) return;

        const map = {};
        const rows = Array.isArray(res.data) ? res.data : [];

        rows.forEach((category) => {
          map[category.id] = category;
        });

        setRoomCategoryMap(map);
      } catch (_err) {
        if (!active) return;
        setRoomCategoryMap({});
      }
    }

    if (businessUnitId) {
      fetchCategories();
    }

    return () => {
      active = false;
    };
  }, [businessUnitId, crossTabRefreshToken]);

  useEffect(() => {
    let active = true;

    async function fetchSummary() {
      setLoading(true);
      setError("");

      try {
        const horizonDates = buildDateRange(startDate, AVAILABILITY_HORIZON_DAYS);
        const dateIndexByIso = new Map(horizonDates.map((iso, idx) => [iso, idx]));
        const businessUnitNum = Number(businessUnitId);

        const forecastRequest = axios.get("/api/admin/forecast-occupancy");
        const roomBlocksRequest = axios.get("/api/room-blocks", {
          params: { business_unit_id: businessUnitId },
        });
        const roomsRequest = axios.get("/api/rooms", {
          params: { business_unit_id: businessUnitId },
        });
        const eventsRequest = axios.get("/api/city-events", {
          params: { business_unit_id: businessUnitId },
        });

        const [forecastRes, roomBlocksRes, roomsRes, eventsRes] = await Promise.all([
          forecastRequest,
          roomBlocksRequest,
          roomsRequest,
          eventsRequest.catch(() => ({ data: [] })),
        ]);

        if (!active) return;

        const forecastRows = Array.isArray(forecastRes?.data) ? forecastRes.data : [];
        const roomBlocksRows = Array.isArray(roomBlocksRes?.data) ? roomBlocksRes.data : [];
        const roomRows = Array.isArray(roomsRes?.data) ? roomsRes.data : [];

        const roomCountsByCategory = {};
        const localCategoryPatch = {};
        roomRows.forEach((room) => {
          const categoryId = room?.room_category_id;
          if (categoryId == null) return;

          const key = String(categoryId);
          roomCountsByCategory[key] = (roomCountsByCategory[key] || 0) + 1;

          const categoryName = room?.RoomCategory?.name;
          if (categoryName) {
            localCategoryPatch[key] = { id: categoryId, name: categoryName };
          }
        });

        if (Object.keys(localCategoryPatch).length > 0) {
          setRoomCategoryMap((prev) => ({ ...prev, ...localCategoryPatch }));
        }

        const categoryIdSet = new Set(Object.keys(roomCountsByCategory));

        forecastRows.forEach((row) => {
          const rowBu = Number(row?.business_unit_id);
          if (Number.isFinite(businessUnitNum) && Number.isFinite(rowBu) && rowBu !== businessUnitNum) return;
          if (row?.room_category_id == null) return;
          categoryIdSet.add(String(row.room_category_id));
        });

        roomBlocksRows.forEach((block) => {
          const categoryId =
            block?.room?.room_category_id ??
            block?.room_category_id ??
            null;
          if (categoryId == null) return;
          categoryIdSet.add(String(categoryId));
        });

        const summaryRows = Array.from(categoryIdSet).map((categoryIdStr) => {
          const inventoryValue = Math.max(0, Number(roomCountsByCategory[categoryIdStr] || 0));
          const inventory = Array(horizonDates.length).fill(inventoryValue);
          const activeSeries = Array(horizonDates.length).fill(inventoryValue);
          const availableSeries = Array(horizonDates.length).fill(inventoryValue);

          return {
            category_id: Number.isFinite(Number(categoryIdStr)) ? Number(categoryIdStr) : categoryIdStr,
            inventory,
            active: activeSeries,
            available: availableSeries,
          };
        });

        const summaryByCategoryId = new Map(
          summaryRows.map((row) => [String(row.category_id), row])
        );

        roomBlocksRows.forEach((block) => {
          const categoryId =
            block?.room?.room_category_id ??
            block?.room_category_id ??
            null;
          if (categoryId == null) return;

          const row = summaryByCategoryId.get(String(categoryId));
          if (!row) return;

          const startIso = normalizeDate(block?.start_date);
          const endIso = normalizeDate(block?.end_date);
          if (!startIso || !endIso) return;

          const cursor = new Date(`${startIso}T00:00:00Z`);
          const endDate = new Date(`${endIso}T00:00:00Z`);
          if (Number.isNaN(cursor.getTime()) || Number.isNaN(endDate.getTime())) return;

          while (cursor <= endDate) {
            const iso = cursor.toISOString().split("T")[0];
            const idx = dateIndexByIso.get(iso);
            if (idx !== undefined) {
              row.active[idx] = Math.max(0, toNumber(row.active[idx], 0) - 1);
              row.available[idx] = Math.max(0, toNumber(row.available[idx], 0) - 1);
            }
            cursor.setUTCDate(cursor.getUTCDate() + 1);
          }
        });

        forecastRows.forEach((forecastRow) => {
          const rowBu = Number(forecastRow?.business_unit_id);
          if (Number.isFinite(businessUnitNum) && Number.isFinite(rowBu) && rowBu !== businessUnitNum) return;

          const categoryId = forecastRow?.room_category_id;
          if (categoryId == null) return;

          const row = summaryByCategoryId.get(String(categoryId));
          if (!row) return;

          const stayDate = normalizeDate(forecastRow?.stay_date);
          const idx = dateIndexByIso.get(stayDate);
          if (idx === undefined) return;

          row.available[idx] = Math.max(0, toNumber(row.available[idx], 0) - 1);
        });

        setDates(horizonDates);
        setAvailabilityRows(summaryRows);
        setCityEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      } catch (err) {
        if (!active) return;

        setAvailabilityRows([]);
        setDates([]);
        setCityEvents([]);
        setError(err?.response?.data?.error || loadErrorMessageRef.current);
      } finally {
        if (!active) return;
        setLoading(false);
      }
    }

    if (businessUnitId) {
      fetchSummary();
    }

    return () => {
      active = false;
    };
  }, [businessUnitId, startDate, crossTabRefreshToken]);

  const categoryRows = useMemo(() => {
    const rows = Array.isArray(availabilityRows) ? availabilityRows : [];

    return rows
      .map((category) => {
        const categoryId = category?.category_id;
        const inventorySeries = Array.isArray(category?.inventory) ? category.inventory : [];
        const activeSeries = Array.isArray(category?.active) ? category.active : [];
        const availableSeries = Array.isArray(category?.available) ? category.available : [];

        const firstInventory = inventorySeries.find((value) => Number.isFinite(Number(value)));
        const fixedInventory = Math.max(0, toNumber(firstInventory, 0));

        return {
          categoryId,
          categoryName:
            roomCategoryMap[categoryId]?.name || availabilityCopy.category.fallback(categoryId),
          fixedInventory,
          inventorySeries,
          activeSeries,
          availableSeries,
        };
      })
      .sort((a, b) =>
        String(a.categoryName).localeCompare(String(b.categoryName), calendarLocale, {
          sensitivity: "base",
        })
      );
  }, [availabilityCopy.category, availabilityRows, calendarLocale, roomCategoryMap]);

  const hotelTotalsByDate = useMemo(() => {
    return dates.map((_, index) => {
      let total = 0;
      let sellable = 0;
      let available = 0;
      let blocked = 0;
      let sold = 0;

      categoryRows.forEach((row) => {
        const daily = getDailyStats(row, index);
        total += daily.total;
        sellable += daily.sellable;
        available += daily.available;
        blocked += daily.blocked;
        sold += daily.sold;
      });

      return {
        total,
        sellable,
        available,
        blocked,
        sold,
        occupancyPct: total > 0 ? Math.round((sold / total) * 100) : 0,
      };
    });
  }, [categoryRows, dates]);

  const maxStartIndex = Math.max(0, dates.length - daysPerPage);

  useEffect(() => {
    setWindowStartIndex((prev) => Math.min(prev, maxStartIndex));
  }, [maxStartIndex]);

  const visibleIndexes = useMemo(() => {
    const indexes = [];
    const end = Math.min(dates.length, windowStartIndex + daysPerPage);

    for (let i = windowStartIndex; i < end; i += 1) {
      indexes.push(i);
    }

    return indexes;
  }, [dates.length, daysPerPage, windowStartIndex]);

  const visibleEventBands = useMemo(() => {
    if (!EXPERIMENTAL_HEADER_EVENT_BANDS || visibleIndexes.length === 0) return [];

    const visibleDates = visibleIndexes.map((index) => dates[index]);
    const visibleStart = visibleDates[0];
    const visibleEnd = visibleDates[visibleDates.length - 1];
    const dateToPosition = new Map(visibleDates.map((date, pos) => [date, pos]));

    const overlaps = (Array.isArray(cityEvents) ? cityEvents : [])
      .map((event, eventIndex) => {
        const start = normalizeDate(event?.start_date);
        const rawEnd = normalizeDate(event?.end_date) || start;
        if (!start) return null;

        const end = rawEnd < start ? start : rawEnd;
        if (end < visibleStart || start > visibleEnd) return null;

        const clippedStart = start < visibleStart ? visibleStart : start;
        const clippedEnd = end > visibleEnd ? visibleEnd : end;
        const startPos = dateToPosition.get(clippedStart);
        const endPos = dateToPosition.get(clippedEnd);

        if (!Number.isInteger(startPos) || !Number.isInteger(endPos)) return null;

        const impact = String(event?.impact_level || "medium").toLowerCase();
          const name =
            String(event?.name || availabilityCopy.specialDate).trim() || availabilityCopy.specialDate;

        return {
          id: `${name}-${clippedStart}-${clippedEnd}-${eventIndex}`,
          name,
          impact: IMPACT_RANK[impact] ? impact : "medium",
          clippedStart,
          clippedEnd,
          startPos,
          endPos,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const byStart = a.startPos - b.startPos;
        if (byStart !== 0) return byStart;

        const bySpan = b.endPos - b.startPos - (a.endPos - a.startPos);
        if (bySpan !== 0) return bySpan;

        const byImpact = (IMPACT_RANK[b.impact] || 0) - (IMPACT_RANK[a.impact] || 0);
        if (byImpact !== 0) return byImpact;

        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      });

    if (overlaps.length === 0) return [];

    const laneLastEnd = Array.from({ length: MAX_SPECIAL_STRIP_LANES }, () => -1);
    const placed = [];
    const hidden = [];

    overlaps.forEach((event) => {
      const lane = laneLastEnd.findIndex((lastEnd) => event.startPos > lastEnd);

      if (lane === -1) {
        hidden.push(event);
        return;
      }

      laneLastEnd[lane] = event.endPos;
      placed.push({
        ...event,
        lane,
        hiddenCount: 0,
        names: [event.name],
      });
    });

    hidden.forEach((event) => {
      let target = null;
      let bestOverlap = -1;

      placed.forEach((candidate) => {
        const overlapStart = Math.max(event.startPos, candidate.startPos);
        const overlapEnd = Math.min(event.endPos, candidate.endPos);
        const overlap = overlapEnd >= overlapStart ? overlapEnd - overlapStart + 1 : -1;

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          target = candidate;
        }
      });

      if (!target) return;

      target.hiddenCount += 1;
      if (!target.names.includes(event.name)) {
        target.names.push(event.name);
      }

      if ((IMPACT_RANK[event.impact] || 0) > (IMPACT_RANK[target.impact] || 0)) {
        target.impact = event.impact;
      }
    });

    return placed.map((segment) => {
      const extra = Number(segment.hiddenCount || 0);
      const label = clipEventLabel(extra > 0 ? `${segment.name} +${extra}` : segment.name);

      return {
        ...segment,
        label,
        tooltip: buildSpecialSegmentTooltip(segment, availabilityCopy),
        gridColumnStart: segment.startPos + 2,
        gridColumnEnd: segment.endPos + 3,
      };
    });
  }, [availabilityCopy, cityEvents, dates, visibleIndexes]);

  const visibleEventLaneCount = useMemo(() => {
    if (visibleEventBands.length === 0) return 0;
    return visibleEventBands.reduce((maxLane, band) => Math.max(maxLane, band.lane), 0) + 1;
  }, [visibleEventBands]);

  const stripGridTemplate = `var(--as-category-col-width) repeat(${visibleIndexes.length}, minmax(0, 1fr))`;

  const canPrev = windowStartIndex > 0;
  const canNext = windowStartIndex < maxStartIndex;

  const rangeLabel =
    visibleIndexes.length > 0
      ? `${dates[visibleIndexes[0]]} -> ${dates[visibleIndexes[visibleIndexes.length - 1]]}`
      : availabilityCopy.noDates;

  const loadedRangeLabel =
    dates.length > 0
      ? `${dates[0]} -> ${dates[dates.length - 1]}`
      : availabilityCopy.noDatesLoaded;

  const subtitle = loading
    ? availabilityCopy.subtitleLoading
    : availabilityCopy.subtitleLoaded(categoryRows.length, loadedRangeLabel);
  const todayDateIso = referenceDate;

  return (
    <div ref={pageRef} className={`as-page ${isLightTheme ? "as-page--light" : ""}`}>
      <div className="as-card">
        <div className="as-cardHeader">
          <div className="as-headingBlock">
            <h2 className="as-title">{availabilityCopy.title}</h2>
            <div className="as-subtitle">{subtitle}</div>
          </div>

          <div className="as-toolbar">
            <div className="as-navGroup">
              <button
                type="button"
                className="as-btn as-btn--icon"
                disabled={!canPrev}
                onClick={() =>
                  setWindowStartIndex((prev) => Math.max(0, prev - daysPerPage))
                }
                aria-label={availabilityCopy.navigation.backDays(daysPerPage)}
                title={availabilityCopy.navigation.backDays(daysPerPage)}
              >
                <FiChevronsLeft size={16} aria-hidden="true" />
              </button>

              <button
                type="button"
                className="as-btn as-btn--icon"
                disabled={!canPrev}
                onClick={() => setWindowStartIndex((prev) => Math.max(0, prev - 1))}
                aria-label={availabilityCopy.navigation.backOneDay}
                title={availabilityCopy.navigation.backOneDay}
              >
                <FiChevronLeft size={16} aria-hidden="true" />
              </button>

              <span className="as-range">{rangeLabel}</span>

              <button
                type="button"
                className="as-btn as-btn--icon"
                disabled={!canNext}
                onClick={() => setWindowStartIndex((prev) => Math.min(maxStartIndex, prev + 1))}
                aria-label={availabilityCopy.navigation.forwardOneDay}
                title={availabilityCopy.navigation.forwardOneDay}
              >
                <FiChevronRight size={16} aria-hidden="true" />
              </button>

              <button
                type="button"
                className="as-btn as-btn--icon"
                disabled={!canNext}
                onClick={() =>
                  setWindowStartIndex((prev) =>
                    Math.min(maxStartIndex, prev + daysPerPage)
                  )
                }
                aria-label={availabilityCopy.navigation.forwardDays(daysPerPage)}
                title={availabilityCopy.navigation.forwardDays(daysPerPage)}
              >
                <FiChevronsRight size={16} aria-hidden="true" />
              </button>

              <button
                type="button"
                className="as-btn as-btn--primary"
                onClick={() => {
                  setStartDate(referenceDate);
                  setWindowStartIndex(0);
                }}
                title={availabilityCopy.navigation.resetToday}
              >
                <FiCalendar size={14} aria-hidden="true" />
                {availabilityCopy.navigation.today}
              </button>
            </div>

            <div className="as-horizonGroup">
              <button
                type="button"
                className="as-btn"
                onClick={() => {
                  setStartDate((prev) => addDaysIso(prev, -daysPerPage));
                  setWindowStartIndex(0);
                }}
                title={availabilityCopy.navigation.loadPrevious(daysPerPage)}
              >
                <FiMinus size={14} aria-hidden="true" />
                {daysPerPage}d
              </button>

              <button
                type="button"
                className="as-btn"
                onClick={() => {
                  setStartDate((prev) => addDaysIso(prev, daysPerPage));
                  setWindowStartIndex(0);
                }}
                title={availabilityCopy.navigation.loadNext(daysPerPage)}
              >
                <FiPlus size={14} aria-hidden="true" />
                {daysPerPage}d
              </button>
            </div>
          </div>
        </div>

        <div className="as-contextBar">
          <div className="as-legend" aria-label={availabilityCopy.title}>
            <span className="as-legendItem">
              <span className="as-swatch as-swatch--available" aria-hidden="true" />
              {availabilityCopy.legend.available}
            </span>
            <span className="as-legendItem">
              <span className="as-swatch as-swatch--sold" aria-hidden="true" />
              {availabilityCopy.legend.sold}
            </span>
            <span className="as-legendItem">
              <span className="as-swatch as-swatch--blocked" aria-hidden="true" />
              {availabilityCopy.legend.blocked}
            </span>
          </div>
          <div className="as-readingHint">
            <FiInfo size={14} aria-hidden="true" />
            <span>{availabilityCopy.readingHint}</span>
          </div>
        </div>

        <div className="as-tableWrap">
          {loading ? (
            <div className="as-state" role="status" aria-live="polite">
              <div className="as-spinner" aria-hidden="true" />
              <div className="as-stateSubtitle">{availabilityCopy.states.loading}</div>
            </div>
          ) : error ? (
            <div className="as-state" role="alert">
              <div className="as-stateIcon" aria-hidden="true">
                <FiAlertCircle size={20} />
              </div>
              <div className="as-stateTitle">{availabilityCopy.states.loadErrorTitle}</div>
              <div className="as-error">{error}</div>
            </div>
          ) : categoryRows.length === 0 || visibleIndexes.length === 0 ? (
            <div className="as-state">
              <div className="as-stateIcon" aria-hidden="true">
                <FiInfo size={20} />
              </div>
              <div className="as-stateTitle">{availabilityCopy.states.noDataTitle}</div>
              <div className="as-stateSubtitle">{availabilityCopy.states.noDataSubtitle}</div>
            </div>
          ) : (
            <>
              {visibleEventLaneCount > 0 ? (
                <div className="as-specialStrip" aria-label={availabilityCopy.specialDates}>
                  {Array.from({ length: visibleEventLaneCount }).map((_, lane) => (
                    <div
                      key={`event-lane-${lane}`}
                      className="as-specialStripLane"
                      style={{ gridTemplateColumns: stripGridTemplate }}
                    >
                      <div className="as-specialStripSpacer" aria-hidden="true" />
                      {visibleEventBands
                        .filter((segment) => segment.lane === lane)
                        .map((segment) => (
                          <span
                            key={segment.id}
                            className={`as-eventBand as-eventBand--${impactClass(segment.impact)} as-eventBand--strip`}
                            style={{ gridColumn: `${segment.gridColumnStart} / ${segment.gridColumnEnd}` }}
                            title={segment.tooltip}
                          >
                            {segment.label}
                          </span>
                        ))}
                    </div>
                  ))}
                </div>
              ) : null}

              <table className="as-table">
                <thead>
                  <tr className="as-headerMain">
                    <th className="as-colCategory">{availabilityCopy.headers.roomCategory}</th>

                    {visibleIndexes.map((index) => {
                      const date = dates[index];
                      const isToday = date === todayDateIso;
                      const isWeekend = isWeekendIso(date);
                      const totals = hotelTotalsByDate[index] || {
                        total: 0,
                        available: 0,
                        blocked: 0,
                        sold: 0,
                        occupancyPct: 0,
                      };

                      return (
                        <th
                          key={date}
                          className={`as-colDay ${isToday ? "as-colDay--today" : ""} ${
                            isWeekend ? "as-colDay--weekend" : ""
                          }`}
                        >
                          <div
                            className={`as-hotelOcc as-hotelOcc--${occupancyToneClass(
                              totals.occupancyPct
                            )}`}
                            title={availabilityCopy.hotel.occupancyTitle(
                              totals.occupancyPct,
                              totals.sold,
                              totals.total
                            )}
                          >
                            {availabilityCopy.hotel.occupancyShort(totals.occupancyPct)}
                          </div>
                          <div className="as-dateLabel">{toDayLabel(date, calendarLocale)}</div>
                          <div
                            className="as-hotelMeta"
                            title={availabilityCopy.hotel.availabilityTitle(
                              totals.available,
                              totals.total,
                              totals.blocked
                            )}
                          >
                            {availabilityCopy.hotel.availabilityShort(
                              totals.available,
                              totals.total
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {categoryRows.map((row) => (
                    <tr key={String(row.categoryId)}>
                      <td className="as-categoryCell">
                        <div className="as-categoryName" title={row.categoryName}>
                          {row.categoryName}
                        </div>
                        <div className="as-categoryMeta">
                          {availabilityCopy.category.inventory(row.fixedInventory)}
                        </div>
                      </td>

                      {visibleIndexes.map((index) => {
                        const date = dates[index];
                        const daily = getDailyStats(row, index);
                        const isCritical = daily.total > 0 && daily.available === 0;
                        const isToday = date === todayDateIso;
                        const isWeekend = isWeekendIso(date);

                        const total = daily.total;
                        const soldPct = total > 0 ? (daily.sold / total) * 100 : 0;
                        const blockedPct = total > 0 ? (daily.blocked / total) * 100 : 0;
                        const availablePct = total > 0 ? (daily.available / total) * 100 : 0;

                        const tooltip = availabilityCopy.cellTooltip({
                          categoryName: row.categoryName,
                          date,
                          total: daily.total,
                          sellable: daily.sellable,
                          available: daily.available,
                          sold: daily.sold,
                          blocked: daily.blocked,
                        });

                        return (
                          <td
                            key={`${row.categoryId}-${date}`}
                            className={`as-dayCell ${
                              isCritical ? "as-dayCell--critical" : ""
                            } ${isToday ? "as-dayCell--today" : ""} ${
                              isWeekend ? "as-dayCell--weekend" : ""
                            }`}
                            title={tooltip}
                          >
                            <div className="as-dayNumber">{daily.available}</div>
                            <div className="as-dayCaption">
                              {availabilityCopy.day.caption(daily.sold, daily.blocked)}
                            </div>

                            <div
                              className={`as-meter ${isCritical ? "as-meter--critical" : ""}`}
                              role="img"
                              aria-label={tooltip}
                            >
                              <span
                                className="as-meterSeg as-meterSeg--available"
                                style={{ width: `${availablePct}%` }}
                              />
                              <span
                                className="as-meterSeg as-meterSeg--sold"
                                style={{ width: `${soldPct}%` }}
                              />
                              <span
                                className="as-meterSeg as-meterSeg--blocked"
                                style={{ width: `${blockedPct}%` }}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilitySummary;
